import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { tenantApi } from '@/api/endpoints';
import { useLangStore } from '@/stores/langStore';
import { useT, type LabelKey } from '@/i18n/labels';
import type { Tenant } from '@/types';

const headerStyle: React.CSSProperties = {
  height: 56,
  padding: '0 1.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid #e5e7eb',
  background: '#ffffff',
};

const rightStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  fontSize: '0.875rem',
};

/** Label key for each canonical main role. */
const MAIN_ROLE_LABEL_KEYS: Record<string, LabelKey> = {
  platformAdmin: 'roleLabelPlatformAdmin',
  admin: 'roleLabelAdmin',
  seniorAgent: 'roleLabelSeniorAgent',
  agent: 'roleLabelAgent',
};

export const Header: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const { isPlatformAdmin, mainRole, isDeveloper } = usePermission();
  const { logout } = useAuth();
  const { selectedTenantId, setSelectedTenantId } = useTenantStore();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const { t } = useT();
  const toggleLang = useLangStore((s) => s.toggle);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    tenantApi
      .list(1, 100)
      // `?? []` is defense-in-depth: the Header renders OUTSIDE the inner
      // ErrorBoundary, so a setTenants(undefined) here would crash the WHOLE app,
      // not just the content area. Never let the switcher's data shape do that.
      .then((res) => setTenants(res.data.items ?? []))
      .catch(() => {
        /* best-effort */
      });
  }, [isPlatformAdmin]);

  const roleLabel = MAIN_ROLE_LABEL_KEYS[mainRole]
    ? t(MAIN_ROLE_LABEL_KEYS[mainRole])
    : mainRole;

  return (
    <header style={headerStyle}>
      <div style={{ fontWeight: 600, color: '#1f2937' }}>{t('appName')}</div>

      <div style={rightStyle}>
        {/* Language toggle — flips the whole unified portal between en/he + RTL
            (ADR-012 i18n unification). Kept in the shell Header (no SignalR
            dependency) so it's available on every screen; the workspace's own
            hub-status + availability controls live inside the workspace module
            so the shell doesn't import the lazy chat bundle. */}
        <button
          type="button"
          data-testid="lang-toggle"
          onClick={toggleLang}
          style={{
            background: 'transparent',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            padding: '0.375rem 0.625rem',
            fontSize: '0.8125rem',
            cursor: 'pointer',
          }}
        >
          {t('languageToggle')}
        </button>

        {/* My Settings — every authenticated role (no permission check). */}
        <Link
          to="/my-settings"
          data-testid="header-my-settings-link"
          style={{
            color: '#374151',
            textDecoration: 'none',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            padding: '0.375rem 0.625rem',
            fontSize: '0.8125rem',
          }}
        >
          {t('headerMySettings')}
        </Link>

        {/* Tenant switcher — Platform Admin only */}
        {isPlatformAdmin && (
          <select
            value={selectedTenantId ?? ''}
            onChange={(e) => setSelectedTenantId(e.target.value || null)}
            aria-label={t('headerSelectTenant')}
            style={{
              padding: '0.375rem 0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: 4,
              fontSize: '0.8125rem',
            }}
          >
            <option value="">{t('headerAllTenants')}</option>
            {/* The reserved platform tenant (ADR-009) cannot be impersonated —
                it has no customer-facing assets and "switching into" it would
                operate on the empty platform plane. Filter it out of the picker
                only; it still appears on the /tenants management list. */}
            {(tenants ?? [])
              .filter((tenant) => !tenant.isPlatformTenant)
              .map((tenant) => (
                <option key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.name}
                </option>
              ))}
          </select>
        )}

        <span style={{ color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
          {user?.displayName} ({roleLabel})
          {isDeveloper && (
            <span
              data-testid="dev-chip"
              title={t('headerDevChipTitle')}
              style={{
                padding: '0.0625rem 0.375rem',
                background: '#0e7490',
                color: '#fff',
                borderRadius: 4,
                fontSize: '0.625rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
              }}
            >
              DEV
            </span>
          )}
        </span>

        <button
          onClick={logout}
          style={{
            padding: '0.375rem 0.75rem',
            background: '#ef4444',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: '0.8125rem',
          }}
        >
          {t('headerLogout')}
        </button>
      </div>
    </header>
  );
};
